import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const token = authService.getToken();

  // Don't add token to public auth endpoints (login, register, refresh)
  const isPublicAuthEndpoint = 
    req.url.includes('/auth/login') || 
    req.url.includes('/auth/register') ||
    req.url.includes('/auth/refresh');

  // Admin endpoints use a separate MDT admin session; a 401 from them means the
  // downstream MDT session expired, NOT the user JWT. Refreshing the user JWT
  // would be pointless and parallel refreshes can race-invalidate the refresh
  // token family, logging the user out.
  const isAdminEndpoint =
    req.url.includes('/api/admin/') ||
    req.url.includes('/api/admin-auth/');

  if (token && !isPublicAuthEndpoint) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only attempt to refresh the session if the request was authenticated
      // to begin with. Otherwise an unauthenticated request (e.g. add-to-cart
      // by an anonymous visitor) would silently re-establish a previous
      // session via the still-present refresh cookie.
      if (error.status === 401 && !isPublicAuthEndpoint && !!token && !isAdminEndpoint) {
        // Try to refresh the token
        return authService.refreshToken().pipe(
          switchMap(result => {
            if (result) {
              // Retry the request with the new token
              const newReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${result.accessToken}`
                }
              });
              return next(newReq);
            }
            return throwError(() => error);
          }),
          catchError(() => throwError(() => error))
        );
      }
      return throwError(() => error);
    })
  );
};
